import { appStorage } from './safeStorage';
import { namespacedKey, subscribeGuideIdentity, setGuideIdentity } from './guideNamespace';
// BLOCK S7 3 — remembers the active floating How-To guide per SIGNED-IN
// account, so the card survives page changes and a reload but NEVER bleeds
// across accounts in a shared browser. Every browser-storage key (active
// guide, position, manual step ticks) is built by the SHARED helper in
// guideNamespace (reused by the inline "How this works" strips too), so both
// guide layers scope to the same identity. The OLD un-namespaced keys
// ("staffed_active_guide_v1", "staffed_guide_manual_*") are no longer read, so
// nothing carries over after this ships. All storage stays in try/catch.

const BASE_KEY = 'staffed_active_guide_v1';
const MANUAL_PREFIX = 'staffed_guide_manual';
const listeners = new Set();

let current = null;

const activeKey = () => namespacedKey(BASE_KEY);

const read = () => {
  const k = activeKey();
  if (!k) return null;
  try {
    const raw = appStorage.getItem(k);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const write = (state) => {
  const k = activeKey();
  if (!k) return; // no identity yet -> keep in-memory only, never a fixed key
  try {
    if (state) appStorage.setItem(k, JSON.stringify(state));
    else appStorage.removeItem(k);
  } catch { /* storage may be unavailable — feature still works in-session */ }
};

// Seed from whatever namespace guideNamespace resolved synchronously (token).
current = read();

const emit = () => {
  listeners.forEach((fn) => { try { fn(current); } catch { /* no-op */ } });
  try { window.dispatchEvent(new CustomEvent('staffed:guide:changed')); } catch { /* no-op */ }
};

// On login / logout / account switch, drop the in-memory guide and re-read
// under the new namespace so a previous account's guide never renders.
subscribeGuideIdentity(() => { current = read(); emit(); });

export const getActiveGuide = () => current;

export const subscribeGuide = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// Re-exported so the card has one import surface; the real logic lives in the
// shared guideNamespace helper (single source of identity).
export { setGuideIdentity };

export const startGuide = (guideId, eventId, eventName) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  current = {
    guideId,
    eventId: eventId || (current && current.eventId) || null,
    eventName: eventName || (current && current.eventName) || null,
    // Under 640px the card opens as a bottom sheet and starts minimised.
    state: isMobile ? 'minimised' : 'open',
    pos: null,
  };
  write(current);
  emit();
};

export const closeGuide = () => {
  current = null;
  write(null);
  emit();
};

export const setGuideState = (state) => {
  if (!current) return;
  current = { ...current, state };
  write(current);
  emit();
};

export const setGuidePosition = (pos) => {
  if (!current) return;
  current = { ...current, pos };
  write(current);
  // Position changes are frequent during a drag — persist but don't spam
  // subscribers/re-renders (the card owns its own live position).
};

export const setGuideEvent = (eventId, eventName) => {
  if (!current || current.eventId === eventId) return;
  current = { ...current, eventId, eventName: eventName || null };
  write(current);
  emit();
};

// Manual step ticks live locally (no server field for progress) — namespaced by
// identity exactly like the active guide, via the SAME shared helper.
const manualKey = (guideId, eventId) => namespacedKey(`${MANUAL_PREFIX}_${guideId}_${eventId || 'none'}`);

export const getManualDone = (guideId, eventId) => {
  const k = manualKey(guideId, eventId);
  if (!k) return {};
  try {
    const raw = appStorage.getItem(k);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

export const setManualDone = (guideId, eventId, stepIndex, done) => {
  const map = getManualDone(guideId, eventId);
  map[stepIndex] = !!done;
  const k = manualKey(guideId, eventId);
  if (k) { try { appStorage.setItem(k, JSON.stringify(map)); } catch { /* no-op */ } }
  return map;
};
